import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Share,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native'
import QRCode from 'react-native-qrcode-svg'
import { useRouter } from 'expo-router'
import type { Href } from 'expo-router'
import { useTheme } from '../../../theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useSnackbar } from '../../../shared/hooks/useSnackbar'
import { useDialog } from '../../../shared/hooks/useDialog'
import { extractErrorMessage } from '../../../../helpers/apiClient.helper'
import { useSupabaseAuth } from '../../../shared/contexts/SupabaseAuthContext'
import { getAccountMembersTabStyles } from './AccountMembersTab.styles'
import {
  ACCOUNT_ADMIN_INVITE_LINK_QR_SIZE,
  ACCOUNT_MEMBERS_TAB_TEST_IDS,
} from '../../constants/accountAdmin.constants'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import {
  buildOrgMemberPrimaryInviteLink,
  formatB2bMemberJoinedLabel,
  getB2bMemberTableNameAndEmail,
} from '../../helpers/b2bAccount.helper'
import { applyMembersDirectoryFilters } from '../../helpers/b2bMembersDirectory.helper'
import {
  MEMBERS_DIRECTORY_ROLE_FILTER,
  MEMBERS_DIRECTORY_SORT,
  type MembersDirectoryRoleFilter,
  type MembersDirectorySortOption,
} from '../../constants/accountMembersDirectory.constants'
import type { MemberActionPending } from '../../hooks/useAccountAdminDialogs'
import { flushToPaint } from '../../helpers/flushToPaint.helper'
import type { B2bMemberRow } from '../../types/b2bAccount.types'
import { copyToClipboardFallback } from '../../helpers/clipboard.helper'
import { AccountMemberTableRow } from './AccountMemberTableRow'
import { AccountMembersDirectoryControls } from './AccountMembersDirectoryControls'

interface AccountMembersTabProps {
  joinCode: string
  /** When set, invitation link / QR use World Cup squad join (Option A). */
  worldCupSquadInviteCode?: string | null
  /** Linked World Cup squad id — when set, shows "View squad results" preview button for admins. */
  linkedWorldCupSquadId?: string | null
  seatUsed: number
  seatMax: number
  /** Plan tier — FREE orgs are always capped at 100 regardless of stored maxSeats. */
  planTier?: string
  members: B2bMemberRow[]
  currentUserId: string
  onRemoveMember: (userId: string) => Promise<void>
  onPromoteToAdmin: (userId: string) => Promise<void>
  onDemoteFromAdmin: (userId: string) => Promise<void>
  memberActionPending: MemberActionPending | null
  /** Bulk remove (org admin); optional — hides bulk UI when omitted. */
  onRemoveMembersBulk?: (
    userIds: string[],
  ) => Promise<{ removed: number; skipped: { userId: string; reason: string }[] } | void>
  /** Web site shell: parent ScrollView scrolls; avoid nested ScrollView clipping. */
  embedInSiteWebOuterScroll?: boolean
}

export const AccountMembersTab: React.FC<AccountMembersTabProps> = ({
  joinCode,
  worldCupSquadInviteCode = null,
  linkedWorldCupSquadId = null,
  seatUsed,
  seatMax,
  members,
  currentUserId,
  onRemoveMember,
  onPromoteToAdmin,
  onDemoteFromAdmin,
  memberActionPending,
  onRemoveMembersBulk,
  embedInSiteWebOuterScroll = false,
}) => {
  const copy = useAccountAdminCopy()
  const appTheme = useTheme()
  const router = useRouter()
  const { width: windowWidth } = useWindowDimensions()
  const isNarrow = windowWidth < 600
  const theme = appTheme
  const styles = useMemo(() => getAccountMembersTabStyles(theme, isNarrow), [theme, isNarrow])
  const { showSnackbar } = useSnackbar()
  const { showDialog } = useDialog()
  const { user: sessionUser } = useSupabaseAuth()
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [bulkRemoveBusy, setBulkRemoveBusy] = useState(false)
  const [directorySearchQuery, setDirectorySearchQuery] = useState('')
  const [directoryRoleFilter, setDirectoryRoleFilter] = useState<MembersDirectoryRoleFilter>(
    MEMBERS_DIRECTORY_ROLE_FILTER.ALL,
  )
  const [directorySortOption, setDirectorySortOption] = useState<MembersDirectorySortOption>(
    MEMBERS_DIRECTORY_SORT.DEFAULT,
  )
  const memberMutationBusy = memberActionPending != null
  const membersDirectoryBusy = memberMutationBusy || bulkRemoveBusy

  const viewerIdentity = useMemo(() => {
    if (!sessionUser) return null
    return {
      id: sessionUser.id,
      firstName: sessionUser.firstName,
      lastName: sessionUser.lastName,
      username: sessionUser.username,
      email: sessionUser.primaryEmailAddress?.emailAddress ?? null,
    }
  }, [sessionUser])

  const inviteLink = useMemo(
    () =>
      buildOrgMemberPrimaryInviteLink({
        orgJoinCode: joinCode,
        worldCupSquadInviteCode,
      }),
    [joinCode, worldCupSquadInviteCode],
  )
  const wcLinked = Boolean(worldCupSquadInviteCode?.trim())
  const inviteHint = wcLinked ? copy.INVITE_LINK_HINT_WC : copy.INVITE_LINK_HINT
  const addMembersIntro = wcLinked
    ? copy.ADD_MEMBERS_BODY_WC_SINGLE_PRIMARY
    : copy.ADD_MEMBERS_BODY
  const adminCount = useMemo(
    () => members.filter((m) => m.role === 'ADMIN').length,
    [members],
  )
  const displayedMembers = useMemo(
    () =>
      applyMembersDirectoryFilters({
        members,
        searchQuery: directorySearchQuery,
        roleFilter: directoryRoleFilter,
        sortOption: directorySortOption,
        viewer: viewerIdentity,
      }),
    [members, directorySearchQuery, directoryRoleFilter, directorySortOption, viewerIdentity],
  )

  useEffect(() => {
    const visibleIds = new Set(displayedMembers.map((m) => m.userId))
    setSelectedIds((prev) => {
      let changed = false
      const next = new Set<string>()
      for (const id of prev) {
        if (visibleIds.has(id)) {
          next.add(id)
        } else {
          changed = true
        }
      }
      if (!changed) return prev
      return next
    })
  }, [displayedMembers])

  const selectableMemberIds = useMemo(
    () => displayedMembers.filter((m) => m.userId !== currentUserId).map((m) => m.userId),
    [displayedMembers, currentUserId],
  )

  const copyText = useCallback(
    async (text: string, successMessage: string) => {
      try {
        if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
          } else {
            copyToClipboardFallback(text)
          }
        } else {
          await Share.share({ message: text })
        }
        showSnackbar(successMessage, 'success')
      } catch {
        /* cancelled */
      }
    },
    [showSnackbar],
  )

  const handleCopyInviteLink = useCallback(async () => {
    await copyText(inviteLink, copy.MEMBERS_INVITE_LINK_COPIED)
  }, [copyText, inviteLink])

  const handleCopyInviteLinkPress = useCallback(() => {
    void handleCopyInviteLink()
  }, [handleCopyInviteLink])

  const handleViewLinkedSquadPress = useCallback(() => {
    if (!linkedWorldCupSquadId) return
    router.push(`/app/worldcup/groups/${linkedWorldCupSquadId}` as Href)
  }, [linkedWorldCupSquadId, router])

  const handleToggleSelect = useCallback((userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (selectableMemberIds.length === 0) return new Set()
      const allOn = selectableMemberIds.every((id) => prev.has(id))
      if (allOn) return new Set()
      return new Set(selectableMemberIds)
    })
  }, [selectableMemberIds])

  const handleCancelBulkSelect = useCallback(() => {
    setSelectionMode(false)
    setSelectedIds(new Set())
  }, [])

  const allSelectableSelected =
    selectableMemberIds.length > 0 &&
    selectableMemberIds.every((id) => selectedIds.has(id))

  const handleBulkRemove = useCallback(async () => {
    if (!onRemoveMembersBulk || selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    const ok = await showDialog({
      title: copy.MEMBERS_BULK_REMOVE_TITLE,
      message: copy.MEMBERS_BULK_REMOVE_MESSAGE,
      type: 'danger',
      confirmText: copy.MEMBERS_BULK_REMOVE_CONFIRM,
      cancelText: copy.MEMBERS_BULK_CANCEL_DIALOG,
    })
    if (!ok) return
    await flushToPaint(() => setBulkRemoveBusy(true))
    try {
      const result = await onRemoveMembersBulk(ids)
      const removed = result && 'removed' in result ? result.removed : 0
      const skipped = result && 'skipped' in result ? result.skipped.length : 0
      showSnackbar(
        skipped > 0
          ? copy.membersBulkRemoveSummary(removed, skipped)
          : copy.membersBulkRemoveDone(removed),
        skipped > 0 ? 'warning' : 'success',
      )
    } catch (e) {
      showSnackbar(
        extractErrorMessage(e, copy.MEMBERS_BULK_REMOVE_FAILED),
        'error',
      )
    } finally {
      setBulkRemoveBusy(false)
      setSelectedIds(new Set())
      setSelectionMode(false)
    }
  }, [onRemoveMembersBulk, selectedIds, showDialog, showSnackbar])

  const membersBody = (
    <>
      <View style={[styles.majorBlock, styles.majorBlockBeforeDirectory]}>
        <Text style={styles.title}>{copy.ADD_MEMBERS_TITLE}</Text>
        <Text style={styles.addMembersIntro}>{addMembersIntro}</Text>
        <Text style={[styles.title, styles.inviteTitle]}>{copy.INVITE_LINK}</Text>
        <Text style={styles.seatText}>
          {copy.SEATS}: {seatUsed}/{seatMax}
        </Text>
        <Text style={styles.inviteHint}>{inviteHint}</Text>
        <Text style={styles.inviteLinkText} selectable>
          {inviteLink}
        </Text>
        <Text style={styles.inviteQrHint}>{copy.INVITE_LINK_QR_HINT}</Text>
        <View
          style={styles.inviteQrWrap}
          accessible
          accessibilityRole="image"
          accessibilityLabel={copy.INVITE_LINK_QR_A11Y}
        >
          <QRCode
            value={inviteLink}
            size={ACCOUNT_ADMIN_INVITE_LINK_QR_SIZE}
            color={theme.gray[900]}
            backgroundColor={theme.background.cardElevated}
          />
        </View>
        <View style={styles.inviteLinkActionsRow}>
          <Pressable
            accessibilityRole="button"
            onPress={handleCopyInviteLinkPress}
            style={({ pressed, hovered }) => [
              styles.secondaryButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.secondaryButtonPressed,
              hovered && Platform.OS === 'web' && styles.secondaryButtonHover,
            ]}
          >
            <Text style={styles.secondaryButtonText}>{copy.COPY_INVITE_LINK}</Text>
          </Pressable>
          {linkedWorldCupSquadId ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={copy.VIEW_LINKED_SQUAD_A11Y}
              onPress={handleViewLinkedSquadPress}
              style={({ pressed, hovered }) => [
                styles.secondaryButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.secondaryButtonPressed,
                hovered && Platform.OS === 'web' && styles.secondaryButtonHover,
              ]}
            >
              <Text style={styles.secondaryButtonText}>{copy.VIEW_LINKED_SQUAD}</Text>
            </Pressable>
          ) : null}
        </View>
      </View>

      <View
        style={styles.sectionDivider}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />

      <View style={styles.membersSection}>
        <Text style={styles.title}>{copy.MEMBERS_DIRECTORY}</Text>
        <Text style={styles.directoryHint}>{copy.MEMBERS_ADMIN_HINT}</Text>
        {members.length > 0 ? (
          <AccountMembersDirectoryControls
            searchQuery={directorySearchQuery}
            onSearchQueryChange={setDirectorySearchQuery}
            roleFilter={directoryRoleFilter}
            onRoleFilterChange={setDirectoryRoleFilter}
            sortOption={directorySortOption}
            onSortOptionChange={setDirectorySortOption}
            shownCount={displayedMembers.length}
            totalCount={members.length}
            disabled={membersDirectoryBusy}
          />
        ) : null}
        {onRemoveMembersBulk ? (
          <View style={styles.bulkToolbar}>
            {!selectionMode ? (
              <Pressable
                accessibilityRole="button"
                disabled={membersDirectoryBusy}
                onPress={() => setSelectionMode(true)}
                style={({ pressed, hovered }) => [
                  styles.bulkToolbarBtn,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && !membersDirectoryBusy && styles.secondaryButtonPressed,
                  hovered && Platform.OS === 'web' && !membersDirectoryBusy && styles.secondaryButtonHover,
                  membersDirectoryBusy && styles.bulkToolbarBtnDisabled,
                ]}
              >
                <Text style={styles.bulkToolbarBtnText}>{copy.MEMBERS_BULK_SELECT}</Text>
              </Pressable>
            ) : (
              <>
                <Pressable
                  accessibilityRole="button"
                  disabled={bulkRemoveBusy}
                  onPress={handleCancelBulkSelect}
                  style={({ pressed, hovered }) => [
                    styles.bulkToolbarBtn,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && !bulkRemoveBusy && styles.secondaryButtonPressed,
                    hovered && Platform.OS === 'web' && !bulkRemoveBusy && styles.secondaryButtonHover,
                    bulkRemoveBusy && styles.bulkToolbarBtnDisabled,
                  ]}
                >
                  <Text style={styles.bulkToolbarBtnText}>{copy.MEMBERS_BULK_CANCEL_SELECT}</Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  disabled={selectedIds.size === 0 || bulkRemoveBusy}
                  accessibilityState={{ busy: bulkRemoveBusy }}
                  accessibilityLabel={
                    bulkRemoveBusy
                      ? copy.MEMBERS_BULK_REMOVE_LOADING_A11Y
                      : undefined
                  }
                  onPress={() => void handleBulkRemove()}
                  style={({ pressed, hovered }) => [
                    styles.bulkToolbarBtn,
                    styles.bulkToolbarBtnPrimary,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && selectedIds.size > 0 && !bulkRemoveBusy && styles.secondaryButtonPressed,
                    hovered &&
                      Platform.OS === 'web' &&
                      selectedIds.size > 0 &&
                      !bulkRemoveBusy &&
                      styles.secondaryButtonHover,
                    (selectedIds.size === 0 || bulkRemoveBusy) && styles.bulkToolbarBtnDisabled,
                  ]}
                >
                  {bulkRemoveBusy ? (
                    <ActivityIndicator size="small" color={theme.white} style={{ marginRight: 6 }} />
                  ) : null}
                  <Text style={[styles.bulkToolbarBtnText, styles.bulkToolbarBtnTextPrimary]}>
                    {copy.MEMBERS_BULK_REMOVE}
                    {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                  </Text>
                </Pressable>
              </>
            )}
          </View>
        ) : null}
        <View style={styles.tableOuter}>
          <View style={styles.tableFrame}>
            <View
              style={styles.tableHeaderRow}
              testID={ACCOUNT_MEMBERS_TAB_TEST_IDS.TABLE_HEADER}
            >
              {!selectionMode ? (
                <View style={styles.tableHeaderIndex}>
                  <Text style={styles.tableHeaderCell}>{copy.MEMBERS_TABLE_INDEX}</Text>
                </View>
              ) : null}
              {selectionMode ? (
                <View style={styles.tableHeaderCheckbox}>
                  <Pressable
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: allSelectableSelected }}
                    accessibilityLabel={copy.MEMBERS_BULK_SELECT_ALL_A11Y}
                    disabled={selectableMemberIds.length === 0 || membersDirectoryBusy}
                    onPress={handleToggleSelectAll}
                    style={({ pressed, hovered }) => [
                      styles.checkboxHitArea,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && selectableMemberIds.length > 0 && styles.secondaryButtonPressed,
                      hovered &&
                        Platform.OS === 'web' &&
                        selectableMemberIds.length > 0 &&
                        styles.secondaryButtonHover,
                      selectableMemberIds.length === 0 && styles.bulkToolbarBtnDisabled,
                    ]}
                  >
                    <View
                      style={[
                        styles.checkboxOuter,
                        allSelectableSelected && styles.checkboxOuterSelected,
                      ]}
                    >
                      {allSelectableSelected ? <View style={styles.checkboxInnerDot} /> : null}
                    </View>
                  </Pressable>
                </View>
              ) : null}
              <View style={styles.tableHeaderName}>
                <Text style={styles.tableHeaderCell}>{copy.MEMBERS_TABLE_NAME}</Text>
              </View>
              <View style={styles.tableHeaderEmail}>
                <Text style={styles.tableHeaderCell}>{copy.MEMBERS_TABLE_EMAIL}</Text>
              </View>
              <View style={styles.tableHeaderRole}>
                <Text style={styles.tableHeaderCell}>{copy.MEMBERS_TABLE_ROLE}</Text>
              </View>
              <View style={styles.tableHeaderJoined}>
                <Text style={styles.tableHeaderCell}>{copy.MEMBERS_TABLE_JOINED}</Text>
              </View>
              <View style={styles.tableHeaderActionsWrap}>
                <Text style={styles.tableHeaderCell}>{copy.MEMBERS_TABLE_ACTIONS}</Text>
              </View>
            </View>
            {members.length === 0 ? (
              <Text style={styles.tableEmpty}>{copy.MEMBERS_EMPTY}</Text>
            ) : displayedMembers.length === 0 ? (
              <Text style={styles.tableEmpty}>{copy.MEMBERS_DIRECTORY_NO_RESULTS}</Text>
            ) : (
              <ScrollView
                testID={ACCOUNT_MEMBERS_TAB_TEST_IDS.TABLE_SCROLL_BODY}
                style={styles.tableScrollBody}
                showsVerticalScrollIndicator
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
              >
                {displayedMembers.map((m, index) => (
                  <AccountMemberTableRow
                    key={m.userId}
                    index={index + 1}
                    member={m}
                    currentUserId={currentUserId}
                    adminCount={adminCount}
                    nameEmail={getB2bMemberTableNameAndEmail(m, viewerIdentity)}
                    joinedLabel={formatB2bMemberJoinedLabel(m.joinedAt)}
                    roleLabel={
                      m.role === 'ADMIN'
                        ? copy.ROLE_LABEL_ADMIN
                        : copy.ROLE_LABEL_MEMBER
                    }
                    isAdminRole={m.role === 'ADMIN'}
                    isLastRow={index === displayedMembers.length - 1}
                    styles={styles}
                    isNarrow={isNarrow}
                    selectionMode={selectionMode}
                    selected={selectedIds.has(m.userId)}
                    onToggleSelect={handleToggleSelect}
                    onPromoteToAdmin={onPromoteToAdmin}
                    onDemoteFromAdmin={onDemoteFromAdmin}
                    onRemoveMember={onRemoveMember}
                    memberActionPending={memberActionPending}
                    membersDirectoryBusy={membersDirectoryBusy}
                    theme={theme}
                  />
                ))}
              </ScrollView>
            )}
          </View>
        </View>
      </View>
    </>
  )

  if (embedInSiteWebOuterScroll) {
    return (
      <View style={[styles.embeddedBody, styles.scrollContentCore]}>{membersBody}</View>
    )
  }

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[styles.scrollContentCore, styles.scrollContentBottomPad]}
      showsVerticalScrollIndicator={Platform.OS === 'web'}
      keyboardShouldPersistTaps="handled"
    >
      {membersBody}
    </ScrollView>
  )
}
